import { useEffect, useRef, useState } from 'react'
import { version as currentVersion } from '../../package.json'

interface VersionInfo {
    version: string
    buildTime: string
}

interface UseAppVersionReturn {
    currentVersion: string
    latestVersion: string | null
    isUpdateAvailable: boolean
    checkForUpdate: () => Promise<void>
    reloadApp: () => void
}

/**
 * Hook para gerenciar versão do app e detectar atualizações
 *
 * Funcionalidades:
 * - Registra service worker para cache busting
 * - Verifica periodicamente se há nova versão disponível
 * - Compara versão local com versão no servidor
 * - Permite reload manual do app
 */
export function useAppVersion(): UseAppVersionReturn {
    const [latestVersion, setLatestVersion] = useState<string | null>(null)
    const [isUpdateAvailable, setIsUpdateAvailable] = useState(false)
    const serviceWorkerRef = useRef<ServiceWorkerRegistration | null>(null)
    const checkIntervalRef = useRef<number | null>(null)

    // Registra o service worker
    useEffect(() => {
        if ('serviceWorker' in navigator && import.meta.env.PROD) {
            registerServiceWorker()
        }

        return () => {
            if (checkIntervalRef.current) {
                clearInterval(checkIntervalRef.current)
            }
        }
    }, [])

    // Registra o service worker
    const registerServiceWorker = async () => {
        try {
            const registration = await navigator.serviceWorker.register(
                '/service-worker.js',
                { scope: '/' },
            )

            serviceWorkerRef.current = registration

            console.log('[App] Service Worker registrado com sucesso')

            // Escuta mensagens do service worker
            navigator.serviceWorker.addEventListener(
                'message',
                handleServiceWorkerMessage,
            )

            // Verifica atualizações quando o service worker atualiza
            registration.addEventListener('updatefound', () => {
                const newWorker = registration.installing
                if (newWorker) {
                    newWorker.addEventListener('statechange', () => {
                        if (
                            newWorker.state === 'installed' &&
                            navigator.serviceWorker.controller
                        ) {
                            // Nova versão disponível
                            checkForUpdate()
                        }
                    })
                }
            })

            // Verifica versão imediatamente após registro
            await checkForUpdate()

            // Configura verificação periódica (a cada 5 minutos)
            checkIntervalRef.current = window.setInterval(
                checkForUpdate,
                5 * 60 * 1000,
            )
        } catch (error) {
            console.error('[App] Erro ao registrar Service Worker:', error)
        }
    }

    // Processa mensagens do service worker
    const handleServiceWorkerMessage = (event: MessageEvent) => {
        if (event.data && event.data.type === 'VERSION_CHECK') {
            const serverVersion = event.data.version
            if (serverVersion && serverVersion !== currentVersion) {
                setLatestVersion(serverVersion)
                setIsUpdateAvailable(true)
                console.log('[App] Nova versão disponível:', serverVersion)
            }
        }
    }

    // Verifica se há atualização disponível
    const checkForUpdate = async () => {
        try {
            // Adiciona timestamp para evitar cache
            const timestamp = new Date().getTime()
            const response = await fetch(`/meta.json?t=${timestamp}`, {
                cache: 'no-store',
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    Pragma: 'no-cache',
                },
            })

            if (response.ok) {
                const meta: VersionInfo = await response.json()

                setLatestVersion(meta.version)

                // Compara versões
                if (meta.version !== currentVersion) {
                    setIsUpdateAvailable(true)
                    console.log('[App] Atualização detectada:', {
                        current: currentVersion,
                        latest: meta.version,
                        buildTime: meta.buildTime,
                    })
                } else {
                    setIsUpdateAvailable(false)
                }
            }
        } catch (error) {
            console.error('[App] Erro ao verificar atualização:', error)
        }
    }

    // Recarrega o app e limpa cache
    const reloadApp = () => {
        // Limpa todos os caches
        if ('caches' in window) {
            caches.keys().then(names => {
                names.forEach(name => {
                    caches.delete(name)
                })
            })
        }

        // Se há um service worker ativo, força atualização
        if (serviceWorkerRef.current?.waiting) {
            serviceWorkerRef.current.waiting.postMessage({
                type: 'SKIP_WAITING',
            })
        }

        // Aguarda um pouco para o service worker processar, então recarrega
        setTimeout(() => {
            window.location.reload()
        }, 100)
    }

    return {
        currentVersion,
        latestVersion,
        isUpdateAvailable,
        checkForUpdate,
        reloadApp,
    }
}
